import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/utils";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, ArrowLeft, CheckCircle2, AlertCircle, Upload, Clock, FileCheck, Trash2, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import axios from "axios";



const InsurancePage = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [docName, setDocName] = useState("");
  const [expiryDate, setExpiryDate] = useState("");

  useEffect(() => { fetchDocs(); }, []);

  const fetchDocs = async () => {
    try {
      const res = await axios.get(`${API}/professional/documents`, { withCredentials: true });
      setDocs((res.data.documents || []).filter((d) => d.doc_type === "insurance"));
    } catch { setDocs([]); }
    setLoading(false);
  };

  const handleUpload = async () => {
    if (!file) { toast.error("Please select a file"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await axios.post(`${API}/upload/document`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      await axios.post(`${API}/professional/documents`, {
        doc_type: "insurance", doc_name: docName || "Insurance Document", file_url: uploadRes.data.url || uploadRes.data.file_url || "", expiry_date: expiryDate || null,
      }, { withCredentials: true });
      toast.success("Insurance document uploaded");
      setShowUpload(false); setFile(null); setDocName(""); setExpiryDate("");
      fetchDocs();
    } catch (err) { toast.error(getErrorMessage(err, "Upload failed")); }
    setUploading(false);
  };

  const handleDelete = async (docId) => {
    try { await axios.delete(`${API}/professional/documents/${docId}`, { withCredentials: true }); toast.success("Removed"); fetchDocs(); } catch { toast.error("Failed"); }
  };

  const expiringDocs = docs.filter((d) => { if (!d.expiry_date) return false; const days = (new Date(d.expiry_date) - new Date()) / (1000 * 60 * 60 * 24); return days > 0 && days <= 30; });

  return (
    <div className="min-h-screen bg-stone-50" data-testid="insurance-page">
      <Navbar />
      <div className="pt-24 pb-16 px-4 max-w-3xl mx-auto">
        <Link to="/professional/dashboard" className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-950 mb-6 transition-colors text-sm" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Work Hub
        </Link>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-emerald-950 flex items-center gap-2"><Shield className="w-6 h-6 text-blue-600" /> Insurance</h1>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full" onClick={() => setShowUpload(!showUpload)} data-testid="upload-insurance-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Upload Proof
            </Button>
          </div>

          {/* Renewal Reminders */}
          {expiringDocs.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="renewal-warning">
              <Bell className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800 text-sm">Renewal Reminder</p>
                {expiringDocs.map((d) => <p key={d.doc_id} className="text-xs text-amber-700">{d.doc_name} expires {new Date(d.expiry_date).toLocaleDateString()}</p>)}
              </div>
            </div>
          )}

          {/* Upload Form */}
          {showUpload && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="bg-white rounded-2xl border border-stone-100 p-6 mb-6" data-testid="upload-form">
              <h3 className="font-semibold text-emerald-950 mb-4">Upload Insurance Document</h3>
              <div className="space-y-3">
                <div><Label className="text-sm">Document Name</Label><Input value={docName} onChange={(e) => setDocName(e.target.value)} placeholder="e.g., Auto Insurance Policy" data-testid="insurance-name-input" /></div>
                <div><Label className="text-sm">Expiry Date</Label><Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} data-testid="insurance-expiry-input" /></div>
                <div><Label className="text-sm">File</Label><Input type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files?.[0])} data-testid="insurance-file-input" /></div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowUpload(false)} className="flex-1">Cancel</Button>
                  <Button onClick={handleUpload} disabled={uploading || !file} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-insurance-btn">{uploading ? "Uploading..." : "Upload"}</Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Platform Coverage */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5 mb-4" data-testid="platform-coverage">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center shrink-0"><CheckCircle2 className="w-5 h-5 text-green-600" /></div>
              <div>
                <h3 className="font-semibold text-emerald-950">Stack Platform Protection</h3>
                <p className="text-sm text-stone-500 mt-1">All active professionals are covered by Stack's platform liability protection while on an active job.</p>
                <Badge className="mt-2 bg-green-100 text-green-700">Active Coverage</Badge>
              </div>
            </div>
          </div>

          {/* Coverage Breakdown */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5 mb-4" data-testid="coverage-breakdown">
            <h3 className="font-semibold text-emerald-950 mb-3">What's Covered While On a Job</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3" data-testid="coverage-liability">
                <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5"><Shield className="w-4 h-4 text-blue-600" /></div>
                <div>
                  <p className="text-sm font-semibold text-emerald-950">General Liability — $1M per occurrence</p>
                  <p className="text-xs text-stone-500 mt-0.5">Third-party bodily injury and property damage caused during an accepted Stack job.</p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="coverage-workers-comp">
                <div className="w-8 h-8 bg-rose-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5"><CheckCircle2 className="w-4 h-4 text-rose-600" /></div>
                <div>
                  <p className="text-sm font-semibold text-emerald-950">Occupational Accident — up to $25,000</p>
                  <p className="text-xs text-stone-500 mt-0.5">Medical expenses and disability benefits for injuries sustained on the job.</p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="coverage-cargo">
                <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5"><FileCheck className="w-4 h-4 text-amber-600" /></div>
                <div>
                  <p className="text-sm font-semibold text-emerald-950">Cargo & Goods — up to $5,000</p>
                  <p className="text-xs text-stone-500 mt-0.5">Items transported on an active Stack job (Cargo, Courier, Movers, Haul/Hitch).</p>
                </div>
              </div>
            </div>
            <p className="text-xs text-stone-400 mt-4 leading-relaxed">Coverage activates the moment you accept a job and ends when the job is completed or cancelled. Personal-use trips are excluded.</p>
          </div>

          {/* Accident Reporting Steps */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-4" data-testid="accident-steps">
            <div className="flex items-start gap-3 mb-3">
              <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
              <h3 className="font-semibold text-amber-900">If You're in an Accident</h3>
            </div>
            <ol className="space-y-2 text-sm text-amber-900 list-decimal list-inside ml-1">
              <li>Ensure everyone is safe — call 911 if injured.</li>
              <li>Document the scene with photos and exchange info with other parties.</li>
              <li>File a police report and request a copy of the report number.</li>
              <li>Report the incident to Stack within 24 hours via Support Chat or claims@stackcartcorp.com.</li>
              <li>Submit your claim details + photos + police report through the Stack Support Center.</li>
            </ol>
          </div>

          {/* Claims Process */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5 mb-4" data-testid="claims-process">
            <h3 className="font-semibold text-emerald-950 mb-3">Claims Process</h3>
            <div className="space-y-3">
              <div className="flex gap-3" data-testid="claim-step-1">
                <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center shrink-0 text-emerald-700 font-bold text-xs">1</div>
                <div>
                  <p className="text-sm font-semibold text-emerald-950">Submit Claim</p>
                  <p className="text-xs text-stone-500">Email claims@stackcartcorp.com with job ID, photos, and police report (if applicable).</p>
                </div>
              </div>
              <div className="flex gap-3" data-testid="claim-step-2">
                <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center shrink-0 text-emerald-700 font-bold text-xs">2</div>
                <div>
                  <p className="text-sm font-semibold text-emerald-950">Review (2–5 business days)</p>
                  <p className="text-xs text-stone-500">Our claims team reviews your submission and may request additional documentation.</p>
                </div>
              </div>
              <div className="flex gap-3" data-testid="claim-step-3">
                <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center shrink-0 text-emerald-700 font-bold text-xs">3</div>
                <div>
                  <p className="text-sm font-semibold text-emerald-950">Decision & Payout</p>
                  <p className="text-xs text-stone-500">Approved claims are paid out within 10 business days via Stripe Connect or direct deposit.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Policy Partners */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5 mb-4" data-testid="policy-partners">
            <h3 className="font-semibold text-emerald-950 mb-3">Our Insurance Partners</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="bg-stone-50 rounded-xl p-3 text-center" data-testid="partner-intact">
                <p className="text-sm font-semibold text-emerald-950">Intact Insurance</p>
                <p className="text-[10px] text-stone-500 mt-0.5">General Liability</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-3 text-center" data-testid="partner-wsib">
                <p className="text-sm font-semibold text-emerald-950">WSIB Ontario</p>
                <p className="text-[10px] text-stone-500 mt-0.5">Workers' Comp</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-3 text-center" data-testid="partner-aviva">
                <p className="text-sm font-semibold text-emerald-950">Aviva Canada</p>
                <p className="text-[10px] text-stone-500 mt-0.5">Cargo & Goods</p>
              </div>
            </div>
            <p className="text-xs text-stone-400 mt-3 leading-relaxed">Coverage is provided in partnership with licensed Canadian carriers. Policy details available upon request from claims@stackcartcorp.com.</p>
          </div>

          {/* Uploaded Insurance Documents */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5" data-testid="insurance-documents">
            <h3 className="font-semibold text-emerald-950 mb-4">Your Insurance Documents</h3>
            {loading ? (
              <div className="animate-pulse space-y-3"><div className="h-16 bg-stone-100 rounded-xl" /><div className="h-16 bg-stone-100 rounded-xl" /></div>
            ) : docs.length === 0 ? (
              <div className="bg-stone-50 rounded-xl p-6 text-center">
                <AlertCircle className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                <p className="text-stone-500 text-sm">No insurance documents uploaded</p>
                <p className="text-stone-400 text-xs mt-1">Upload your vehicle or liability insurance for faster dispute resolution</p>
              </div>
            ) : (
              <div className="space-y-2">
                {docs.map((doc) => (
                  <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl" data-testid={`insurance-doc-${doc.doc_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <FileCheck className="w-4 h-4 text-blue-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-stone-700 truncate">{doc.doc_name}</p>
                        <p className="text-xs text-stone-400">{doc.expiry_date ? `Expires ${new Date(doc.expiry_date).toLocaleDateString()}` : "No expiry set"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={doc.status === "approved" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                        {doc.status === "approved" ? "Verified" : "Pending"}
                      </Badge>
                      <button onClick={() => handleDelete(doc.doc_id)} className="p-1.5 text-stone-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InsurancePage;
